/**
 * V4.18 — Explainable Market Signal: "Vergleichsobjekte ansehen".
 *
 * Modal listing the EXACT comparables the market engine used, plus the
 * cohort criteria checklist ("Für diese Bewertung berücksichtigt").
 * Data: GET /api/listings/{ulid}/comparables (read-only).
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Check, ExternalLink, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getComparables } from "../api/dealflowApi";
import { ProGateCard } from "./billing/ProGate";
import { enumLabel } from "../utils/enumLabels";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

function fmtDistance(t, m) {
  if (m == null) return null;
  if (m < 1000) return t("comparables.distanceM", { m });
  return t("comparables.distanceKm", {
    km: (m / 1000).toFixed(1).replace(".", ","),
  });
}

function CompRow({ comp, vertical, t, onOpen }) {
  const meta = (
    vertical === "auto"
      ? [
          comp.year ? t("comparables.firstReg", { year: comp.year }) : null,
          comp.mileage_km != null
            ? `${comp.mileage_km.toLocaleString("de-AT")} km`
            : null,
          comp.fuel ? enumLabel("fuel", comp.fuel) : null,
          comp.gearbox ? enumLabel("gearbox", comp.gearbox) : null,
          comp.power_ps
            ? `${comp.power_ps} PS`
            : comp.power_kw
              ? `${comp.power_kw} kW`
              : null,
          comp.trim && comp.trim !== "base" ? comp.trim.toUpperCase() : null,
          comp.seller_type ? enumLabel("seller_type", comp.seller_type) : null,
        ]
      : [
          comp.living_area_m2 ? `${Math.round(comp.living_area_m2)} m²` : null,
          comp.rooms ? t("comparables.rooms", { count: comp.rooms }) : null,
          comp.year_built
            ? t("comparables.yearBuilt", { year: comp.year_built })
            : null,
          [comp.postal_code, comp.city].filter(Boolean).join(" ") || null,
          comp.seller_type ? enumLabel("seller_type", comp.seller_type) : null,
        ]
  )
    .filter(Boolean)
    .join(" · ");
  const distance = vertical !== "auto" ? fmtDistance(t, comp.distance_m) : null;
  const inactive = comp.status && comp.status !== "active";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(comp.id)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(comp.id)}
      data-testid={`comparable-row-${comp.id}`}
      className="flex gap-3 rounded-xl border border-zinc-800/80 bg-zinc-900/40 p-2.5 hover:border-emerald-500/40 transition-colors group cursor-pointer"
    >
      <div className="w-20 h-16 shrink-0 rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800">
        {comp.image ? (
          <img
            src={comp.image}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-zinc-700 text-lg">
            ◾
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="text-xs text-zinc-300 line-clamp-1">
            {comp.title || "—"}
          </div>
          {comp.url ? (
            <a
              href={comp.url}
              target="_blank"
              rel="noreferrer"
              title={t("comparables.openOriginal")}
              data-testid={`comparable-external-${comp.id}`}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0"
            >
              <ExternalLink className="h-3.5 w-3.5 text-zinc-600 hover:text-emerald-300 transition-colors" />
            </a>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <div className="text-sm font-semibold text-emerald-300">
            {comp.price_display || "—"}
            {vertical !== "auto" && comp.price_per_m2 ? (
              <span className="text-[11px] font-normal text-zinc-500 ml-2">
                {t("comparables.pricePerM2", {
                  value: Math.round(comp.price_per_m2).toLocaleString("de-AT"),
                })}
              </span>
            ) : null}
          </div>
          {distance && (
            <span
              data-testid={`comparable-distance-${comp.id}`}
              className="inline-flex items-center gap-1 shrink-0 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-300"
            >
              <MapPin className="h-3 w-3" />
              {distance}
            </span>
          )}
        </div>
        {meta && (
          <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-1">
            {meta}
          </div>
        )}
        {Array.isArray(comp.match_reasons) && comp.match_reasons.length > 0 && (
          <div
            className="flex flex-wrap gap-1 mt-1"
            data-testid={`comparable-reasons-${comp.id}`}
          >
            {comp.match_reasons.slice(0, 6).map((r) => (
              <span
                key={r}
                className="inline-flex items-center gap-0.5 rounded-full border border-zinc-700/60 bg-zinc-800/60 px-1.5 py-px text-[10px] text-zinc-400"
              >
                <Check className="h-2.5 w-2.5 text-emerald-400" />
                {t(`filters.${r}`, { defaultValue: r })}
              </span>
            ))}
          </div>
        )}
        {inactive && (
          <div className="text-[10px] text-amber-400/70 mt-0.5">
            {t("comparables.inactive")}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ComparablesModal({ ulid, vertical, open, onOpenChange }) {
  const { t } = useTranslation("market");
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["comparables", ulid],
    queryFn: () => getComparables(ulid),
    enabled: open && !!ulid,
    staleTime: 5 * 60 * 1000,
  });
  const cohort = data?.cohort;
  const comps = data?.comparables || [];
  // P0 — split into exact vs. similar sections (backend `match_grade`).
  const exactComps = comps.filter((c) => c.match_grade !== "similar");
  const similarComps = comps.filter((c) => c.match_grade === "similar");
  const validation = cohort?.validation;
  // V4.19/V4.20 — "Primäre Preisbasis" block: exact price basis vs.
  // similar market validation. Auto uses the knowledge-layer primary
  // cohort; immo uses the chosen cascade level (both presentation only).
  const primary = vertical === "auto" ? cohort?.primary_cohort : null;
  const showBasis = vertical === "auto" ? !!primary : exactComps.length > 0;
  const basisCount = vertical === "auto"
    ? (primary?.comparable_count ?? exactComps.length)
    : (cohort?.comparable_count ?? exactComps.length);
  const basisCountLine = vertical === "auto"
    ? `${t("comparables.vehiclesCount", { count: basisCount })}${
        cohort?.primary_label ? ` · ${cohort.primary_label}` : ""}`
    : t("comparables.objectsCount", { count: basisCount });
  const AUTO_SCOPE_KEYS = [
    "same_motorization", "same_generation", "same_model_family",
    "same_trim", "similar_trim_tier",
  ];
  const basisScope = vertical === "auto"
    ? (cohort?.applied_filter_keys || [])
        .filter((k) => AUTO_SCOPE_KEYS.includes(k))
        .slice(0, 2)
        .map((k) => t(`filters.${k}`, { defaultValue: k }))
        .join(" · ")
    : (cohort?.level_name
        ? t(`comparables.scope.${cohort.level_name}`, {
            defaultValue: t(`cohort.${cohort.level_name}`, { defaultValue: "" }),
          })
        : "");
  const similarHeader = showBasis
    ? (vertical === "auto"
        ? t("comparables.similarValidation", { count: similarComps.length })
        : t("comparables.similarValidationImmo", { count: similarComps.length }))
    : `${t("comparables.similarSection")} (${similarComps.length})`;

  const openListing = (id) => {
    onOpenChange(false);
    navigate(`/listing/${id}`);
  };

  let footerLine = null;
  if (validation?.count > 0) {
    footerLine = t("validation.validated", {
      count: validation.count,
      scope: t(`validationScope.${validation.level_name}`, {
        defaultValue: t("validationScope.fallback"),
      }),
    });
  } else if (cohort?.expanded && cohort?.level_name) {
    footerLine = t("validation.expanded", {
      scope: t(`cohort.${cohort.level_name}`, {
        defaultValue: t("cohort.fallback"),
      }),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="comparables-modal"
        className="max-w-lg bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[85vh] flex flex-col p-0 gap-0"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-900 text-left space-y-1">
          <DialogTitle className="text-base text-zinc-100">
            {t("comparables.title")}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {t("comparables.subtitle")}
          </DialogDescription>
          {cohort?.applied_filter_keys?.length > 0 && (
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
                {t("comparables.criteriaTitle")}
              </div>
              <div
                className="flex flex-wrap gap-1.5"
                data-testid="comparables-criteria"
              >
                {cohort.applied_filter_keys.map((k) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 text-[11px] text-emerald-200/90 bg-emerald-500/[0.07] border border-emerald-500/20 rounded-full px-2 py-0.5"
                  >
                    <Check className="h-3 w-3 text-emerald-400" />
                    {t(`filters.${k}`, { defaultValue: k })}
                  </span>
                ))}
              </div>
            </div>
          )}
        </DialogHeader>
        <div
          className="flex-1 overflow-y-auto px-5 py-3 space-y-2.5"
          data-testid="comparables-list"
        >
          {isLoading && (
            <div className="text-sm text-zinc-500 py-6 text-center">
              {t("comparables.loading")}
            </div>
          )}
          {isError && (
            <div className="text-sm text-amber-300/80 py-6 text-center">
              {t("comparables.error")}
            </div>
          )}
          {!isLoading && !isError && comps.length === 0 && (
            <div
              className="text-sm text-zinc-500 py-6 text-center"
              data-testid="comparables-empty"
            >
              {t("comparables.empty")}
            </div>
          )}
          {exactComps.length > 0 && showBasis && (
            <div
              data-testid="comparables-primary-basis"
              className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] px-3 py-2"
            >
              <div className="text-[10px] uppercase tracking-widest text-emerald-400/90">
                {t("comparables.primaryBasisTitle")}
              </div>
              <div
                className="text-sm font-semibold text-zinc-100 mt-0.5"
                data-testid="comparables-primary-basis-count"
              >
                {basisCountLine}
              </div>
              {basisScope && (
                <div
                  className="text-[11px] text-zinc-400 mt-0.5"
                  data-testid="comparables-primary-basis-scope"
                >
                  {basisScope}
                </div>
              )}
            </div>
          )}
          {exactComps.length > 0 && !showBasis && similarComps.length > 0 && (
            <div
              className="text-[10px] uppercase tracking-widest text-emerald-400/80 pt-1"
              data-testid="comparables-exact-section"
            >
              {`${t("comparables.exactSection")} (${exactComps.length})`}
            </div>
          )}
          {exactComps.map((c) => (
            <CompRow key={c.id} comp={c} vertical={vertical} t={t} onOpen={openListing} />
          ))}
          {similarComps.length > 0 && (
            <div
              className="text-[10px] uppercase tracking-widest text-zinc-500 pt-2"
              data-testid="comparables-similar-section"
            >
              {similarHeader}
            </div>
          )}
          {showBasis && similarComps.length > 0 && (
            <div
              className="text-[11px] text-zinc-500 italic"
              data-testid="comparables-similar-note"
            >
              {vertical === "auto"
                ? t("comparables.similarNote")
                : t("comparables.similarNoteImmo")}
            </div>
          )}
          {similarComps.map((c) => (
            <CompRow key={c.id} comp={c} vertical={vertical} t={t} onOpen={openListing} />
          ))}
          {data?.locked && (
            <div className="pt-2" data-testid="comparables-locked-gate">
              <ProGateCard
                compact
                surface="comparables_modal"
                context="comparables"
                lockedCount={Math.max((data.locked_total || 0) - comps.length, 0)}
              />
            </div>
          )}
        </div>
        {footerLine && (
          <div
            className="px-5 py-3 border-t border-zinc-900 text-[11px] text-zinc-500 italic"
            data-testid="comparables-validation-footer"
          >
            {footerLine}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
